import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { of, take } from 'rxjs';
import { InterfaceActionsService } from '../../../../features/admin/modules/features/interface-actions/interface-actions.service';
import { InterfaceAction } from '../../../../features/admin/modules/features/interface-actions/interface-actions.model';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { UtilityService } from '../../../services/utility.service';
import { debugLog } from '../../../utils/debug-logger';
import type { Module } from '../../../../features/admin/modules/modules.model';

@Component({
  selector: 'formly-interface-action-button',
  template: `
    @if (interfaceAction()) {
      <div class="w-full flex">
        <div class="flex-1"></div>
        <button
          mat-flat-button
          type="button"
          class="flex-0 z-0 float-right text-right dd-action-button"
          [matTooltip]="tooltipText()"
          [attr.data-cy]="getDataCy()"
          [disabled]="isExecuting()"
          (click)="executeAction()">
          @if (interfaceAction().icon) {
            <mat-icon class="relative top-0.5 icon-size-3.5" [svgIcon]="interfaceAction().icon"></mat-icon>&nbsp;
          }
          <span>{{ interfaceAction().name }}</span>
        </button>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: inline-block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class InterfaceActionButtonFormlyComponent extends FieldType<FieldTypeConfig> {
  readonly #interfaceActionsService = inject(InterfaceActionsService);
  readonly #modulesStore = inject(ModulesStore);
  readonly #utilityService = inject(UtilityService);

  readonly isExecuting = signal(false);

  /**
   * Get the module that contains the interface actions.
   * Uses interfaceActionModuleCode if specified, otherwise falls back to formState.entity
   */
  #getActionModule(): Module | null {
    const moduleCode = this.props['interfaceActionModuleCode'];

    // Try to find the module by code if specified
    if (moduleCode) {
      const foundModule = this.#modulesStore.enabledModules().find(m => m.code === moduleCode);
      if (foundModule) {
        return foundModule;
      }
    }

    // Fallback to formState.entity for backward compatibility
    return this.options.formState?.entity ?? null;
  }

  /** The configured action, regardless of whether the current user may see it. */
  readonly #configuredAction = computed(() => {
    const actionId = this.props['interfaceActionId'];
    const module = this.#getActionModule();
    return module?.interfaceActions?.find((a: InterfaceAction) => a.id === actionId);
  });

  /**
   * The action to render — undefined when the action's `userRoles` exclude the current user, which
   * hides the button entirely (the template renders nothing without an action).
   *
   * A form-embedded button is configured on the action just like a row/detail button, so it must
   * honour the same role restriction: e.g. an onboarding form's "Stuur naar gebruiker" is meant for
   * the employee filling the form in on someone else's behalf, not for the onboarding user.
   */
  interfaceAction = computed(() => {
    const action = this.#configuredAction();
    return this.#interfaceActionsService.isAllowedForCurrentUser(action) ? action : undefined;
  });

  tooltipText = computed(() => (this.interfaceAction()?.icon ? this.interfaceAction()?.name : ''));

  getDataCy(): string {
    const action = this.interfaceAction();
    return action ? 'interface-button-' + UtilityService.createSlug(action.name) : '';
  }

  /** Walk an AbstractControl tree and return the paths of invalid leaf controls + their errors. */
  #invalidControlPaths(control: AbstractControl, path = ''): Array<{ path: string; errors: unknown }> {
    if (control.valid || control.disabled) return [];

    if (control instanceof FormGroup || control instanceof FormArray) {
      return Object.entries(control.controls).flatMap(([key, child]) =>
        this.#invalidControlPaths(child as AbstractControl, path ? `${path}.${key}` : key)
      );
    }

    // Leaf control that is itself invalid.
    return [{ path: path || '(root)', errors: control.errors }];
  }

  executeAction(): void {
    if (this.isExecuting()) {
      return;
    }

    const action = this.interfaceAction();
    const actionModule = this.#getActionModule();
    const entry = this.options.formState?.entry;

    if (!action || !actionModule || !entry) {
      return;
    }

    // Anti-misuse: only execute if the linked action is actually configured for the formButton context.
    // Without this an action with only 'detail' as location could still be triggered via id-coupling.
    if (!this.#interfaceActionsService.isVisibleInContext(action, 'formButton')) {
      return;
    }

    // Same gate as the render path, restated so a future refactor of `interfaceAction` cannot
    // silently make a role-restricted action runnable.
    if (!this.#interfaceActionsService.isAllowedForCurrentUser(action)) {
      return;
    }

    // Validate only the sub-form the button lives in — not the entire form.
    // - Wizard-step button → the step's FormGroup (only current step)
    // - Pages-page button → the page's FormGroup
    // - Root-level button → the full root form (fallback)
    //
    // This avoids blocking a partial-flow action (e.g. "Verstuur gegevens" on step 6
    // of 7) on fields that belong to steps the user isn't supposed to fill in yet.
    const detailsService = this.options.formState?.detailsService;
    const scopeForm =
      (this.field.parent?.formControl as AbstractControl | undefined) ??
      (detailsService?.form?.() as AbstractControl | undefined);

    if (scopeForm?.invalid) {
      // Mark controls as touched so Formly renders the individual field errors,
      // and surface a toast so the user gets feedback (a display-only field's
      // error is easy to miss, and the button itself stays enabled).
      scopeForm.markAllAsTouched();
      // Log exactly which controls block — a generic toast hides whether it's the
      // field the user expects (e.g. signing) or an unrelated required field.
      debugLog('InterfaceActionButton', 'submit blocked, invalid controls:', this.#invalidControlPaths(scopeForm));
      this.#utilityService.notify('Niet alle verplichte velden zijn correct ingevuld.');
      return;
    }

    this.isExecuting.set(true);

    // Run async validation (no-op when no async validators are configured),
    // then trigger the interface action.
    const asyncValidation$ = detailsService?.validateForm ? detailsService.validateForm() : of(true);
    asyncValidation$.pipe(take(1)).subscribe({
      next: (isValid: boolean) => {
        if (!isValid) {
          this.isExecuting.set(false);
          return;
        }

        // navigateBack false: a button inside a form stays on that form — the history step-back is
        // row-action behavior, not form behavior.
        this.#interfaceActionsService.handleCustomAction(
          { id: action.id, entry, interfaceAction: action },
          actionModule,
          true,
          false
        );

        // handleCustomAction stages actions with setTimeout(2500 * index); release
        // the lock slightly after the longest expected delay so the button is not
        // permanently disabled if execution fails silently.
        setTimeout(() => this.isExecuting.set(false), 3000);
      },
      error: () => {
        this.isExecuting.set(false);
      },
    });
  }
}
